// frontend/src/components/dialogs/OnCallRequestDialog.js
// This component is used to request on-call time for a specific slot
// It displays a modal with a form to select a time and confirm the request
// It also displays existing bookings for the selected slot
// It uses the Modal component to display the modal
// It uses the Clock, LucideSave, and BookX icons from Lucide React

import { useState, useMemo, useEffect, useRef } from 'react';
import Modal from '../Modal';
import { Clock, LucideSave, BookX, MapPin, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatDateISO } from '../../utils/dateTimeFormatter';
import { useDateTimePreferences } from '../../hooks/useDateTimePreferences';
import { useTheme } from '../../contexts/ThemeContext';
import ThemedButton from '../themed/ThemedButton';
import { calculateBookingETA, formatDurationText } from '../../utils/etaCalculations';
import {
    getPickupStartInfo,
    normalizeBookingOccurrenceDate,
} from '../../utils/bookingScheduleUtils';
import bookingEtaService from '../../services/bookingEtaService';

const operationalTimeToMinutes = (value, { allowExclusiveEnd = false } = {}) => {
    const match = String(value || '').match(/^(\d{2}):(\d{2})$/);
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (minutes > 59) return null;
    if (allowExclusiveEnd && hours === 24 && minutes === 0) return 24 * 60;
    if (hours > 23) return null;
    return hours * 60 + minutes;
};

const OnCallRequestDialog = ({
    userRole,
    isOpen,
    onClose,
    onConfirm,
    slot,
    existingBookings,
    slotAvailability = [],
    slotAvailabilityDate = null,
    isExactSelectionSnapshotCurrent = false,
    bookingType,
    selectedDates,
    startDate,
    endDate,
    isProcessing = false,
    waypoints,
    pickupStopId,
    dropoffStopId,
    referenceData,
    segmentData, // Segment times for baseline ETA (OpenRouteService)
    entityId = null,
    ridersRequested = 1,
    luggageRequested = 0,
}) => {
    const { t } = useTranslation();
    // Force re-render when preferences change
    const { formatTime, formatDate, formatTimeString } = useDateTimePreferences();
    const { text, background, border } = useTheme();
    const [requestedTime, setRequestedTime] = useState('');
    const [error, setError] = useState('');
    const etaRef = useRef(null);
    const scrollPendingRef = useRef(false);
    const slotStartTime = slot?.startTime;
    const slotEndTime = slot?.endTime;
    const operationalBlockedIntervals = Array.isArray(slot?.operationalBlockedIntervals)
        ? slot.operationalBlockedIntervals.filter((interval) => {
            const start = operationalTimeToMinutes(interval?.startTime);
            const end = operationalTimeToMinutes(
                interval?.endTime,
                { allowExclusiveEnd: true }
            );
            return start != null && end != null && end > start;
        })
        : [];
    const formatOperationalBoundary = (value) => (
        value === '24:00'
            ? t('operationalUnavailability.endOfDay')
            : formatTimeString(value)
    );

    // Determine if pickup is the start waypoint (independent of etaInfo so it works before user enters time)
    const { isPickupStart, startName } = useMemo(
        () => getPickupStartInfo(waypoints, pickupStopId),
        [waypoints, pickupStopId]
    );

    const blockedTimes = useMemo(() => {
        if (!slotStartTime || !slotEndTime) {
            return new Set();
        }
        const blocked = slotAvailability
            .filter(entry => entry?.time && (
                entry.pickupStopPassed
                || entry.pickup_stop_passed
                || (
                    entry.bookingStopEligibility?.state
                    && entry.bookingStopEligibility.state !== 'available'
                )
            ))
            .filter(entry => entry.time >= slotStartTime && entry.time <= slotEndTime)
            .map(entry => entry.time.substring(0, 5));
        return new Set(blocked);
    }, [slotStartTime, slotEndTime, slotAvailability]);

    const getEligibilityMessage = (entry) => {
        const eligibility = entry?.bookingStopEligibility;
        if (!eligibility || eligibility.state === 'available') return null;
        const endpoints = eligibility.endpoints || [];
        if (endpoints.includes('pickup') && endpoints.includes('dropoff')) {
            return t('bookingStopEligibility.bothUnavailable');
        }
        if (endpoints.includes('pickup')) {
            return t('bookingStopEligibility.pickupUnavailable');
        }
        if (endpoints.includes('dropoff')) {
            return t('bookingStopEligibility.dropoffUnavailable');
        }
        return t('bookingStopEligibility.unresolved');
    };

    const getTripStatusLabel = (booking) => {
        const eligibilityMessage = getEligibilityMessage(booking);
        if (eligibilityMessage) return eligibilityMessage;
        const tripStatus = booking?.tripStatus ?? booking?.trip_status ?? null;
        const pickupStopPassed = Boolean(booking?.pickupStopPassed ?? booking?.pickup_stop_passed ?? false);

        if (pickupStopPassed) {
            return t('tripStatusPickupPassed');
        }

        if (tripStatus === 'dispatched' || tripStatus === 'in_progress') {
            return t('tripStatusOnTheWay');
        }

        if (!tripStatus || tripStatus === 'scheduled') {
            return t('tripStatusNoStartYet');
        }

        if (tripStatus === 'completed') {
            return t('tripStatusCompleted');
        }

        if (tripStatus === 'canceled' || tripStatus === 'cancelled') {
            return t('tripStatusCanceled');
        }

        return t('tripStatusUnknown');
    };

    const slotAvailabilityByTime = useMemo(() => {
        const map = new Map();
        slotAvailability
            .filter(entry => entry?.time)
            .filter(entry => !slotStartTime || !slotEndTime ||
                (entry.time >= slotStartTime && entry.time <= slotEndTime))
            .forEach((entry) => {
                map.set(entry.time.substring(0, 5), entry);
            });
        return map;
    }, [slotStartTime, slotEndTime, slotAvailability]);

    const slotAvailabilityByTripId = useMemo(() => {
        const map = new Map();
        slotAvailability.forEach((entry) => {
            const tripId = Number(entry?.tripId ?? entry?.trip_id);
            if (Number.isInteger(tripId) && tripId > 0) {
                map.set(tripId, entry);
            }
        });
        return map;
    }, [slotAvailability]);

    const getExactTripDelayMinutes = (tripIdValue, occurrenceDate) => {
        const tripId = Number(tripIdValue);
        if (
            !isExactSelectionSnapshotCurrent
            || !Number.isInteger(tripId)
            || tripId <= 0
            || normalizeBookingOccurrenceDate(occurrenceDate)
                !== normalizeBookingOccurrenceDate(slotAvailabilityDate)
        ) {
            return 0;
        }

        const minutes = Number(
            slotAvailabilityByTripId.get(tripId)?.operationalDelay?.originDepartureMinutes,
        );
        return Number.isInteger(minutes) && minutes > 0 ? minutes : 0;
    };

    const activeTripId = useMemo(() => {
        const entry = requestedTime ? slotAvailabilityByTime.get(requestedTime) : null;
        const tripId = Number(entry?.tripId ?? entry?.trip_id);
        return Number.isInteger(tripId) && tripId > 0 ? tripId : null;
    }, [requestedTime, slotAvailabilityByTime]);
    const requestedDelayMinutes = getExactTripDelayMinutes(
        activeTripId,
        slot?.date || selectedDates?.[0],
    );

    const [tripEtaData, setTripEtaData] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const loadTripEtaData = async () => {
            if (!entityId || !activeTripId) {
                setTripEtaData(null);
                return;
            }

            try {
                const data = await bookingEtaService.getTripEtaData(entityId, activeTripId);
                if (!cancelled) {
                    setTripEtaData(data);
                }
            } catch (error) {
                console.warn(`Failed to fetch trip-aware booking ETA data for trip ${activeTripId}:`, error);
                if (!cancelled) {
                    setTripEtaData(null);
                }
            }
        };

        loadTripEtaData();

        return () => {
            cancelled = true;
        };
    }, [activeTripId, entityId]);

    // Handle dialog close and reset state
    const handleClose = () => {
        setRequestedTime('');
        setError('');
        onClose();
    };

    // Handle confirm and reset state
    const handleConfirm = () => {
        if (validateTime()) {
            onConfirm(requestedTime);
            setRequestedTime('');
            setError('');
        }
    };

    // Calculate ETA based on requested time
    const etaInfo = useMemo(() => {
        if (!requestedTime || !waypoints || !pickupStopId || !dropoffStopId) {
            return null;
        }

        // Use the first selected date or today
        const tripDate = selectedDates?.[0] || formatDateISO(new Date());
        const tripWaypoints = tripEtaData?.waypoints;
        const usesTripData =
            tripWaypoints?.length > 0 &&
            tripWaypoints.some(wp => wp.id === pickupStopId) &&
            tripWaypoints.some(wp => wp.id === dropoffStopId);

        return calculateBookingETA(
            usesTripData ? tripWaypoints : waypoints,
            requestedTime,
            tripDate,
            pickupStopId,
            dropoffStopId,
            referenceData, // Pass reference data for traffic ETA
            usesTripData ? tripEtaData?.segments : segmentData    // Keep segments matched to waypoint source
        );
    }, [requestedTime, tripEtaData, waypoints, pickupStopId, dropoffStopId, selectedDates, referenceData, segmentData]);

    useEffect(() => {
        if (!scrollPendingRef.current || !etaInfo) {
            return;
        }

        scrollPendingRef.current = false;
        etaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [etaInfo]);
           
    if (!isOpen || !slot) return null;

    const validateTime = () => {
        const startTime = new Date(`2000-01-01T${slot.startTime}`);
        const endTime = new Date(`2000-01-01T${slot.endTime}`); 
        const requestTime = new Date(`2000-01-01T${requestedTime}`);
        const normalizedRiders = Math.max(parseInt(ridersRequested, 10) || 1, 1);
        const normalizedLuggage = Math.max(parseInt(luggageRequested, 10) || 0, 0);
     
        if (requestTime < startTime || requestTime > endTime) {
          setError(t('Please select a time between {{start}} and {{end}}', {
            start: slot.startTime,
            end: slot.endTime
          }));
          return false;
        }

        if (blockedTimes.has(requestedTime)) {
          setError(
            getEligibilityMessage(slotAvailabilityByTime.get(requestedTime))
            || t('slotDisabledPickupStopPassed')
          );
          return false;
        }

        const requestedMinutes = operationalTimeToMinutes(requestedTime);
        if (requestedMinutes == null) {
          setError(t('Please select a time between {{start}} and {{end}}', {
            start: slot.startTime,
            end: slot.endTime
          }));
          return false;
        }
        const matchingOperationalInterval = operationalBlockedIntervals.find((interval) => {
          const start = operationalTimeToMinutes(interval.startTime);
          const end = operationalTimeToMinutes(
            interval.endTime,
            { allowExclusiveEnd: true }
          );
          return start <= requestedMinutes
            && requestedMinutes < end;
        });
        if (matchingOperationalInterval) {
          setError(t('operationalUnavailability.timeUnavailable', {
            start: formatOperationalBoundary(matchingOperationalInterval.startTime),
            end: formatOperationalBoundary(matchingOperationalInterval.endTime)
          }));
          return false;
        }

        const slotDate = slot?.date || selectedDates?.[0] || formatDateISO(new Date());
        const requestedDateTime = new Date(`${slotDate}T${requestedTime}`);
        const tripEntry = slotAvailabilityByTime.get(requestedTime);
        // Only allow past times if there's an active trip (dispatched/in_progress/scheduled)
        // that hasn't passed the pickup stop yet
        const tripStatus = tripEntry?.tripStatus ?? tripEntry?.trip_status ?? null;
        const pickupPassed = Boolean(tripEntry?.pickupStopPassed ?? tripEntry?.pickup_stop_passed);
        const isActiveTripAvailable = tripEntry &&
          ['dispatched', 'in_progress', 'scheduled'].includes(tripStatus) &&
          !pickupPassed;
        if (!Number.isNaN(requestedDateTime.getTime()) && requestedDateTime < new Date() && !isActiveTripAvailable) {
          setError(t('slotDisabledPast'));
          return false;
        }

        const remainingSeats = Number(
          tripEntry?.availableOnSegment ??
          tripEntry?.available_on_segment ??
          tripEntry?.availableAtPickup ??
          tripEntry?.available_at_pickup ??
          tripEntry?.remainingSeats ??
          tripEntry?.remaining_seats ??
          slot?.remainingSeats ??
          0
        );
        const totalCapacity = Number(
          tripEntry?.vehicleCapacity ??
          tripEntry?.vehicle_capacity ??
          slot?.totalCapacity ??
          0
        );
        const remainingLuggageRaw =
          tripEntry?.availableLuggageOnSegment ??
          tripEntry?.available_luggage_on_segment ??
          tripEntry?.availableLuggageAtPickup ??
          tripEntry?.available_luggage_at_pickup ??
          tripEntry?.remainingLuggage ??
          tripEntry?.remaining_luggage ??
          slot?.remainingLuggage ??
          null;
        const remainingLuggage = Number(remainingLuggageRaw ?? 0);
        const totalLuggageCapacity = Number(
          tripEntry?.vehicleLuggageCapacity ??
          tripEntry?.vehicle_luggage_capacity ??
          slot?.totalLuggageCapacity ??
          0
        );
        const potentialWithReorder = Boolean(
          tripEntry?.potentialWithReorder ??
          tripEntry?.potential_with_reorder ??
          slot?.potentialWithReorder ??
          false
        );

        const hasVehicle = totalCapacity > 0;
        const notEnoughSeats = hasVehicle && normalizedRiders > remainingSeats;
        const hasLuggageCapacity = totalLuggageCapacity > 0;
        const notEnoughLuggage = hasLuggageCapacity && remainingLuggageRaw != null && normalizedLuggage > remainingLuggage;
        if ((notEnoughSeats || notEnoughLuggage) && !potentialWithReorder) {
          const messageKey = notEnoughSeats && notEnoughLuggage
            ? 'slotDisabledInsufficientBoth'
            : notEnoughLuggage
            ? 'slotDisabledInsufficientLuggage'
            : 'slotDisabledInsufficientSeats';
          setError(t(messageKey));
          return false;
        }
     
        const hasConflict = existingBookings?.some(booking => {
          const bookingTime = new Date(`2000-01-01T${booking.time}`);
          const diffMinutes = Math.abs(requestTime - bookingTime) / (1000 * 60);
          return diffMinutes > 0 && diffMinutes < slot.minGap;
        });
     
        if (hasConflict) {
          setError(t('maintainMinimumBookingGap', {
            gap: slot.minGap
          }));
          return false;
        }
     
        setError('');
        return true;
    };

    const renderUserBookings = () => {
        const groupedBookings = existingBookings.reduce((acc, booking) => {
            // Keep date as-is from backend (already YYYY-MM-DD) to avoid timezone conversion issues
            // new Date("2025-12-10") interprets as UTC, causing date shift in Western timezones
            const date = typeof booking.date === 'string' ? booking.date.split('T')[0] : booking.date;
            const time = booking.time.substring(0, 5);
            
            if (!acc[date]) acc[date] = {};
            if (!acc[date][time]) acc[date][time] = [];
            acc[date][time].push(booking);
            
            return acc;
        }, {});

        return (
            <div className="space-y-4">
                {Object.entries(groupedBookings)
                    .sort(([dateA], [dateB]) => new Date(dateA) - new Date(dateB))
                    .map(([date, timeGroups]) => (
                        <div key={date} className={`pb-2 last:border-0 ${border.default} border-b`}>
                            <h5 className={`font-medium text-sm mb-2 ${text.primary}`}>{date}</h5>
                            <div className="space-y-1">
                                {Object.entries(timeGroups)
                                    .sort(([timeA], [timeB]) => timeA.localeCompare(timeB))
                                    .map(([time, bookings]) => {
                                        const rowDelayMinutes = getExactTripDelayMinutes(
                                            bookings?.[0]?.tripId ?? bookings?.[0]?.trip_id,
                                            date,
                                        );
                                        return (
                                        <div
                                            key={`${date}-${time}`}
                                            onClick={() => {
                                                if (blockedTimes.has(time)) {
                                                    return;
                                                }
                                                scrollPendingRef.current = true;
                                                setRequestedTime(time);
                                                setError('');
                                            }}
                                            className={`flex items-center p-2 rounded text-sm cursor-pointer transition-colors
                                                    border border-transparent hover:border-blue-200 ml-4
                                                    ${blockedTimes.has(time) ? 'opacity-50 cursor-not-allowed' : ''}
                                                    ${background.elevated} hover:bg-blue-50 dark:hover:bg-blue-900`}
                                        >
                                            <Clock className={`w-4 h-4 mr-2 ${text.muted}`} />
                                            <span className={`font-medium ${text.primary}`}>{time}</span>
                                            {rowDelayMinutes > 0 && (
                                                <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                                                    {t('bookingSlotDelay.badge', { minutes: rowDelayMinutes })}
                                                </span>
                                            )}
                                            <span className={`mx-2 ${text.muted}`}>•</span>
                                            <span className={text.secondary}>
                                                {getTripStatusLabel({ ...bookings?.[0], time })}
                                            </span>
                                            <span className={`mx-2 ${text.muted}`}>•</span>
                                            <span className={text.secondary}>
                                                {bookings.length > 1
                                                    ? t('{{count}} users booked', { count: bookings.length })
                                                    : t('1 user booked')}
                                            </span>
                                        </div>
                                        );
                                    })
                                }
                            </div>
                        </div>
                    ))}
            </div>
        );
    };

    const renderAdminBookings = () => {
        // Group bookings by date and time to show aggregated capacity info
        const groupedBookings = existingBookings.reduce((acc, booking) => {
            // Keep date as-is from backend (already YYYY-MM-DD) to avoid timezone conversion issues
            const date = typeof booking.date === 'string' ? booking.date.split('T')[0] : booking.date;
            const time = booking.time.substring(0, 5);

            if (!acc[date]) acc[date] = {};
            if (!acc[date][time]) acc[date][time] = [];
            acc[date][time].push(booking);

            return acc;
        }, {});

        return (
            <div className="space-y-4">
                {Object.entries(groupedBookings)
                    .sort(([dateA], [dateB]) => new Date(dateA) - new Date(dateB))
                    .map(([date, timeGroups]) => (
                        <div key={date} className={`pb-2 last:border-0 ${border.default} border-b`}>
                            <h5 className={`font-medium text-sm mb-2 ${text.primary}`}>{date}</h5>
                            <div className="space-y-1">
                                {Object.entries(timeGroups)
                                    .sort(([timeA], [timeB]) => timeA.localeCompare(timeB))
                                    .map(([time, bookings]) => {
                                        // Get time-specific capacity info from slotAvailability
                                        const slotInfo = slotAvailabilityByTime.get(time);
                                        const totalCapacity = slotInfo?.totalCapacity || slotInfo?.vehicleCapacity || slotInfo?.vehicle_capacity || 0;
                                        const remainingSeats = slotInfo?.remainingSeats ?? slotInfo?.remaining_seats ?? slotInfo?.availableOnSegment ?? slotInfo?.available_on_segment ?? 0;
                                        const totalLuggageCapacity = slotInfo?.totalLuggageCapacity || slotInfo?.vehicleLuggageCapacity || slotInfo?.vehicle_luggage_capacity || 0;
                                        const remainingLuggage = slotInfo?.remainingLuggage ?? slotInfo?.remaining_luggage ?? slotInfo?.availableLuggageOnSegment ?? slotInfo?.available_luggage_on_segment ?? 0;
                                        const rowDelayMinutes = getExactTripDelayMinutes(
                                            bookings?.[0]?.tripId ?? bookings?.[0]?.trip_id,
                                            date,
                                        );

                                        return (
                                            <div
                                                key={`${date}-${time}`}
                                                onClick={() => {
                                                    if (blockedTimes.has(time)) {
                                                        return;
                                                    }
                                                    scrollPendingRef.current = true;
                                                    setRequestedTime(time);
                                                    setError('');
                                                }}
                                                className={`flex items-center p-2 rounded text-sm cursor-pointer transition-colors
                                                        border border-transparent hover:border-blue-200 ml-4
                                                        ${blockedTimes.has(time) ? 'opacity-50 cursor-not-allowed' : ''}
                                                        ${background.elevated} hover:bg-blue-50 dark:hover:bg-blue-900`}
                                            >
                                                <Clock className={`w-4 h-4 mr-2 ${text.muted}`} />
                                                <span className={`font-medium ${text.primary}`}>{time}</span>
                                                {rowDelayMinutes > 0 && (
                                                    <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                                                        {t('bookingSlotDelay.badge', { minutes: rowDelayMinutes })}
                                                    </span>
                                                )}
                                                <span className={`mx-2 ${text.muted}`}>•</span>
                                                <span className={text.secondary}>
                                                    {getTripStatusLabel({ ...bookings?.[0], time })}
                                                </span>
                                                {totalCapacity > 0 ? (
                                                    <>
                                                        <span className={`mx-2 ${text.muted}`}>•</span>
                                                        <span className={text.secondary}>
                                                            {t('slotCapacityLabel', {
                                                                remaining: Math.max(remainingSeats, 0),
                                                                total: totalCapacity
                                                            })}
                                                        </span>
                                                        {totalLuggageCapacity > 0 && (
                                                            <>
                                                                <span className={`mx-2 ${text.muted}`}>•</span>
                                                                <span className={text.secondary}>
                                                                    {t('slotLuggageCapacityLabel', {
                                                                        remaining: Math.max(remainingLuggage, 0),
                                                                        total: totalLuggageCapacity
                                                                    })}
                                                                </span>
                                                            </>
                                                        )}
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className={`mx-2 ${text.muted}`}>•</span>
                                                        <span className={text.secondary}>
                                                            {bookings.length > 1
                                                                ? t('{{count}} users booked', { count: bookings.length })
                                                                : t('1 user booked')}
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })
                                }
                            </div>
                        </div>
                    ))}
            </div>
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t('Request On-Call Time')}
            size="md"
            footer={
                <div className="flex justify-end gap-4">
                    <ThemedButton
                        variant="primary"
                        onClick={handleConfirm}
                        disabled={!requestedTime || isProcessing}
                        icon={LucideSave}
                        loading={isProcessing}
                    >
                        {t('Confirm')}
                    </ThemedButton>
                    <ThemedButton
                        variant="secondary"
                        onClick={handleClose}
                        disabled={isProcessing}
                        icon={BookX}
                    >
                        {t('Cancel')}
                    </ThemedButton>
                </div>
            }
        >
            {error && (
                <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
                    {error}
                </div>
            )}

            {bookingType !== 'single' && (
                <div className="mb-4 bg-blue-50 p-2 rounded text-sm">
                    {bookingType === 'all' ? (
                        <p>
                        {t('This time will be applied to all available dates from {{start_date}} to {{end_date}}', {
                          start_date: formatDate(startDate),
                          end_date: formatDate(endDate)
                        })}
                      </p>                    
                    ) : (
                        <p>{t('This time will be applied to {{selected_dates}}', { 
                            selected_dates: (selectedDates || []).map(date => formatDate(date)).join(', ')
                        })}</p>
                    )}
                </div>
            )}

            <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                    {isPickupStart
                        ? t('Enter Your Preferred Time')
                        : t('enterPreferredDepartureTimeWithStart', { startName })}
                </label>
                <input
                    type="time"
                    value={requestedTime}
                    onChange={(e) => {
                        setRequestedTime(e.target.value);
                        setError('');
                    }}
                    min={slot.startTime}
                    max={slot.endTime}
                    className={`w-full p-2 border rounded ${error ? 'border-red-500' : ''}`}
                />
                <p className="text-sm text-gray-500 mt-1">
                    {t('Available between')} {formatTimeString(slot.startTime)} - {formatTimeString(slot.endTime)}
                    ({t('min gap')}: {slot.minGap} {t('min')})
                </p>
                {operationalBlockedIntervals.length > 0 && (
                    <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
                        <div className="flex items-start gap-2">
                            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                            <div>
                                <p className="font-medium">
                                    {t('operationalUnavailability.partialWindowNotice')}
                                </p>
                                <p className="mt-1">
                                    {t('operationalUnavailability.unavailableTimesLabel')}
                                </p>
                                <ul className="mt-1 list-disc space-y-1 pl-5">
                                    {operationalBlockedIntervals.map((interval) => (
                                        <li key={`${interval.startTime}-${interval.endTime}`}>
                                            {formatOperationalBoundary(interval.startTime)}
                                            {' – '}
                                            {formatOperationalBoundary(interval.endTime)}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ETA Information Display */}
            {etaInfo && requestedTime && (etaInfo.baseline?.available || etaInfo.available) && (
                <div
                    ref={etaRef}
                    className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
                >
                    {requestedDelayMinutes > 0 && (
                        <div className="mb-3 flex items-start rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
                            <AlertTriangle className="mr-2 mt-0.5 h-4 w-4 shrink-0" />
                            <p>
                                {t('bookingSlotDelay.etaExclusionNotice', {
                                    minutes: requestedDelayMinutes,
                                })}
                            </p>
                        </div>
                    )}
                    <div className="flex items-start mb-2">
                        <MapPin className="w-4 h-4 mr-2 mt-1 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                        <div className="space-y-2 flex-1">
                            <h4 className="font-medium text-sm text-blue-900 dark:text-blue-200">
                                {t('Estimated Schedule')}:
                            </h4>

                            {/* Departure from start location */}
                            <div className="text-sm text-gray-700 dark:text-gray-300">
                                <span className="font-medium">{t('Departure from')} {etaInfo.startLocation.name}:</span>
                                <span className="ml-2">{formatTime(etaInfo.departureTime)}</span>
                            </div>

                            {/* Arrival at pickup (if different from start) */}
                            {!etaInfo.summary?.isPickupStart && (
                                <div className="text-sm text-gray-700 dark:text-gray-300">
                                    <span className="font-medium">{t('Arrival at')} {etaInfo.pickupLocation?.name} ({t('pickup')}):</span>
                                    <div className="ml-2 space-y-1">
                                        {/* ORS baseline ETA */}
                                        {etaInfo.baseline?.available && (
                                            <div>
                                                <span>{formatTime(etaInfo.baseline.pickupLocation.time)}</span>
                                                <span className="text-gray-500 dark:text-gray-400 ml-1 text-xs">
                                                    ({formatDurationText(etaInfo.baseline.summary.startToPickupMinutes, t)} - {t('No traffic (ORS)')})
                                                </span>
                                            </div>
                                        )}
                                        {/* Effective travel, including complete mixed spans. */}
                                        {etaInfo.available && (
                                            <div>
                                                <span>{formatTime(etaInfo.pickupLocation.time)}</span>
                                                <span className="text-gray-500 dark:text-gray-400 ml-1 text-xs">
                                                    ({formatDurationText(etaInfo.summary.startToPickupMinutes, t)} - {t(etaInfo.reference?.available ? 'Reference route' : 'Estimated Schedule')})
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Arrival at dropoff */}
                            <div className="text-sm text-gray-700 dark:text-gray-300">
                                <span className="font-medium">{t('Arrival at')} {etaInfo.dropoffLocation?.name} ({t('dropoff')}):</span>
                                <div className="ml-2 space-y-1">
                                    {/* ORS baseline ETA */}
                                    {etaInfo.baseline?.available && (
                                        <div>
                                            <span>{formatTime(etaInfo.baseline.dropoffLocation.time)}</span>
                                            <span className="text-gray-500 dark:text-gray-400 ml-1 text-xs">
                                                ({formatDurationText(etaInfo.baseline.summary.totalTripMinutes, t)} {t('total')} - {t('No traffic (ORS)')})
                                            </span>
                                        </div>
                                    )}
                                    {/* Effective travel, including complete mixed spans. */}
                                    {etaInfo.available && (
                                        <div>
                                            <span>{formatTime(etaInfo.dropoffLocation.time)}</span>
                                            <span className="text-gray-500 dark:text-gray-400 ml-1 text-xs">
                                                ({formatDurationText(etaInfo.summary.totalTripMinutes, t)} {t('total')} - {t(etaInfo.reference?.available ? 'Reference route' : 'Estimated Schedule')})
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Warning message */}
                    <div className="flex items-start mt-3 pt-3 border-t border-blue-200 dark:border-blue-700">
                        <AlertTriangle className="w-4 h-4 mr-2 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                            {t('Note: Actual times may vary due to road conditions and traffic')}
                        </p>
                    </div>
                </div>
            )}

            {existingBookings?.length > 0 && (
                <div className="mb-4">
                    <h4 className="font-medium text-sm mb-2">
                        {t('Existing Bookings')} ({t('clickToSelectTime')}):
                    </h4>
                    {userRole === 'user' ? renderUserBookings() : renderAdminBookings()}
                </div>
            )}             
            
        </Modal>
    );
};

export default OnCallRequestDialog;
